import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {TextureFromRevisionSockets} from "#material/nodes/texture/texture-from-revision/definition"
import {textureFromRevisionDefinition} from "#material/nodes/texture/texture-from-revision/definition"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {DataResolver} from "#material/validation/data-resolver/index"
import {TexImage} from "#material/nodes/texture/tex-image/tex-image"
import type {ThreeReturns} from "#material/sockets/three"
import type {MaterialNodeFlattenedReturns} from "#material/sockets/parameters"
import {materialConnection} from "#material/nodes/base/connection/shorthand"

@registerMaterialGraphNode
export class TextureFromRevision extends MaterialNode<TextureFromRevisionSockets> {
    public override get definition() {
        return textureFromRevisionDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<TextureFromRevisionSockets>> => {
        const graph = await this.buildTextureFromRevisionGraph(evaluator.context.resolve)
        return evaluator.getAll(graph)
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<TextureFromRevisionSockets>> => {
        const graph = await this.buildTextureFromRevisionGraph(evaluator.context.resolve)
        return evaluator.getAll(graph)
    }

    override flatten = async (resolve: DataResolver) => {
        return this.buildTextureFromRevisionGraph(resolve)
    }

    async buildTextureFromRevisionGraph(dataResolver: DataResolver): Promise<MaterialNodeFlattenedReturns<TextureFromRevisionSockets>> {
        if (!this.parameters.settings.textureRevisionLegacyId) throw Error("No texture revision legacy ID provided")

        const imageResource = await dataResolver.textureRevisionLegacy(this.parameters.settings.textureRevisionLegacyId)
        if (!imageResource?.dataObjectLegacyId) {
            throw Error(`Legacy texture revision image resource could not be resolved`)
        }

        const {vector} = this.parameters.inputs
        const {
            colorspace,
            disableProgressiveLoading,
            extension,
            forceOriginalResolution,
            imageColorspaceSettingsName,
            interpolation,
            projection,
            vector: settingsVector,
        } = this.parameters.settings

        const texImage = new TexImage({
            parameters: {
                inputs: {
                    vector: vector?.clone({cloneSubNode: () => false}),
                },
                settings: {
                    dataObjectLegacyId: imageResource.dataObjectLegacyId,
                    colorspace,
                    disableProgressiveLoading,
                    extension,
                    forceOriginalResolution,
                    imageColorspaceSettingsName,
                    interpolation,
                    projection,
                    vector: settingsVector ?? undefined,
                },
            },
        })

        return {
            color: materialConnection(texImage, "color"),
            alpha: materialConnection(texImage, "alpha"),
        }
    }
}
